import { Divider, Heading, Text } from '@zillow/constellation-10';
import { Flex, Popper } from '@zillow/constellation';
import React, { useContext } from 'react';
import { ReadRepliedStatus } from '../../types/GraphqlTypes';
import { ModalType } from '../../types/ModalType';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import useModal from '../../hooks/useModal';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import stringUtils from '../../utils/stringUtils';
import RedDot from '../RedDot';
import PropertySearchBar from './components/PropertySearchBar';
import StatusSelector from './components/StatusSelector';
import Subheading from './components/Subheading';
import { IconFilter } from '../Icons';
import { StyledPopover, StyledRedDotContainer, StyledTextButton } from './StyledComponents';

const FiltersPopover = () => {
  const { activeModal, clearModal, setActiveModal } = useModal();
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const { folder, listingAlias, isByProperty, readRepliedStatus, renterProgressStatus } = useContext(InboxPageContext);
  const isPopoverOpen = activeModal === ModalType.FiltersPopover;
  const isUnreadActive = readRepliedStatus === ReadRepliedStatus.Unread;
  const isNotRepliedToActive = readRepliedStatus === ReadRepliedStatus.NotRepliedTo;
  let appliedFiltersCount = 0;

  if (isUnreadActive || isNotRepliedToActive) {
    appliedFiltersCount++;
  }

  if (isByProperty) {
    appliedFiltersCount++;
  }

  const filtersCountDisplay = appliedFiltersCount ? `(${appliedFiltersCount})` : '';
  const filterBtnAriaLabel = `Show property and conversation status filters, ${appliedFiltersCount} ${stringUtils.pluralize(
    'filter',
    appliedFiltersCount,
  )} applied`;

  const handleClearStatusFilter = () => {
    changeFiltersOrFolders({
      folder,
      readRepliedStatus: ReadRepliedStatus.ShowAll,
      renterProgressStatus: renterProgressStatus,
      byProperty: isByProperty,
      listingAlias: listingAlias || '',
    });
  };

  const handleClose = () => clearModal();
  const handleTogglePopover = () => setActiveModal(!isPopoverOpen ? ModalType.FiltersPopover : null);

  return (
    <Popper
      isOpen={isPopoverOpen}
      onClose={handleClose}
      triggered={
        <StyledPopover
          header={
            <Heading level={4} textStyle="body-lg-bold">
              Filter by
            </Heading>
          }
          body={
            <>
              <PropertySearchBar />

              <Divider css={{ marginTop: 'loose', marginBottom: 'default' }} />

              <Subheading
                subtitle="Status"
                clearAriaLabel="Clear status filter selections"
                isClearDisabled={readRepliedStatus === ReadRepliedStatus.ShowAll}
                onClearClick={handleClearStatusFilter}
              />

              <StatusSelector />
            </>
          }
        />
      }
    >
      <StyledTextButton
        aria-label={filterBtnAriaLabel}
        onClick={handleTogglePopover}
        icon={
          <Flex display="flex" alignItems="center">
            <IconFilter />
          </Flex>
        }
      >
        <Text
          css={{
            xlDown: { display: 'none' },
            xl: { display: 'none' },
            xxl: { display: 'inline' },
          }}
        >{`Filters ${filtersCountDisplay}`}</Text>

        {!!appliedFiltersCount && (
          <StyledRedDotContainer>
            <RedDot />
          </StyledRedDotContainer>
        )}
      </StyledTextButton>
    </Popper>
  );
};

export default FiltersPopover;
